import { Box, Button, IconButton, Stack, TextField } from "@mui/material";
import { Add, Delete, ExpandMore } from "@mui/icons-material";
import { useState } from "react";
import { IconSelector } from "./IconSelector";
import { ColorPicker } from "./ColorPicker";
import { TemplateConfig } from "../../../types/template";

interface FolderBuilderProps {
  config: TemplateConfig[];
  onChange: (config: TemplateConfig[]) => void;
}

const FolderBuilder = ({ config, onChange }: FolderBuilderProps) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const handleAdd = (path: string = "") => {
    const newFolder: TemplateConfig = {
      text: "Nueva Carpeta",
      description: "",
      icon: "folder",
      color: "#808080",
      children: [],
    };

    if (!path) {
      onChange([...config, newFolder]);
      return;
    }

    const pathParts = path.split("-");
    const newConfig = JSON.parse(JSON.stringify(config));

    let current = newConfig;
    pathParts.forEach((part, index) => {
      const partNum = parseInt(part);
      if (index === pathParts.length - 1) {
        current[partNum].children?.push(newFolder);
      } else {
        current = current[partNum].children || [];
      }
    });

    onChange(newConfig);
  };

  const handleRemove = (path: string) => {
    if (!path) {
      onChange([]);
      return;
    }

    const pathParts = path.split("-");
    const newConfig = JSON.parse(JSON.stringify(config));

    if (pathParts.length === 1) {
      newConfig.splice(parseInt(pathParts[0]), 1);
      onChange(newConfig);
      return;
    }

    let current = newConfig;
    pathParts.slice(0, -1).forEach((part) => {
      const partNum = parseInt(part);
      current = current[partNum].children || [];
    });

    current.splice(parseInt(pathParts[pathParts.length - 1]), 1);
    onChange(newConfig);
  };

  const handleChange = (
    path: string,
    field: keyof TemplateConfig,
    value: any
  ) => {
    const pathParts = path.split("-");
    const newConfig = JSON.parse(JSON.stringify(config));

    let current = newConfig;
    for (let i = 0; i < pathParts.length; i++) {
      const partNum = parseInt(pathParts[i]);
      if (i === pathParts.length - 1) {
        // Hemos llegado al elemento objetivo
        current[partNum][field] = value;
      } else {
        // Seguimos navegando por la jerarquía
        current = current[partNum].children || [];
      }
    }

    onChange(newConfig);
  };
  const toggleExpand = (path: string) => {
    setExpanded((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  const renderFolder = (
    folder: TemplateConfig,
    index: number,
    path: string = ""
  ) => {
    const currentPath = path ? `${path}-${index}` : `${index}`;
    const hasChildren = folder.children && folder.children.length > 0;

    return (
      <Box key={currentPath} sx={{ ml: path.split("-").length * 4 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          {hasChildren && (
            <IconButton size="small" onClick={() => toggleExpand(currentPath)}>
              <ExpandMore
                sx={{
                  transform: expanded[currentPath]
                    ? "rotate(0deg)"
                    : "rotate(-90deg)",
                  transition: "transform 0.2s",
                }}
              />
            </IconButton>
          )}

          <IconSelector
            value={folder.icon || "folder"}
            onChange={(value) => handleChange(currentPath, "icon", value)}
          />

          <TextField
            size="small"
            value={folder.text}
            onChange={(e) => handleChange(currentPath, "text", e.target.value)}
            sx={{ flex: 1 }}
          />

          <ColorPicker
            value={folder.color || "#808080"}
            onChange={(value) => handleChange(currentPath, "color", value)}
          />

          <IconButton onClick={() => handleAdd(currentPath)}>
            <Add />
          </IconButton>

          <IconButton onClick={() => handleRemove(currentPath)}>
            <Delete color="error" />
          </IconButton>
        </Stack>

        {expanded[currentPath] && hasChildren && (
          <Box sx={{ pl: 4 }}>
            {folder.children?.map((child, childIndex) =>
              renderFolder(child, childIndex, currentPath)
            )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Button startIcon={<Add />} onClick={() => handleAdd()} sx={{ mb: 2 }}>
        Añadir Carpeta Principal
      </Button>

      {config.map((folder, index) => renderFolder(folder, index))}
    </Box>
  );
};

export default FolderBuilder;
